import { ApiEndpoints } from '@shared/api/api-endpoints';
import { communicationApiClient, consumerCommunicationApiClient, cxApiClient } from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { Communication, CommunicationFilters, CommunicationPayload } from '../types/communicationTypes';
import { PaginatedResponse } from '@core/types';

// Communication Types
export interface CommunicationTabFilters extends CommunicationFilters {
  page?: number;
  limit?: number;
  remoteUtilityId?: number;
  communicationType?: string;
  entityId?: number;
  actionType?: string;
  dateRange?: {
    start: string;
    end: string;
  };
  status?: string[];
  channelType?: string;
  searchData?: string;
}

export interface CreateCommunicationPayload {
  remoteUtilityId: number;
  channelType: string;
  message: {
    cc: string[];
    to: string[];
    bcc: string[];
    body: string;
    channel: string;
    subject: string;
    templateId?: number;
  };
  entityId?: number;
  actionType?: string;
  runTime?: string;
  recipientList?: any;
  workflow?: any;
  eventType?: string;
  eventData?: any;
}

export interface UpdateCommunicationPayload {
  channelType?: string;
  message?: {
    cc?: string[];
    to?: string[];
    bcc?: string[];
    body?: string;
    channel?: string;
    subject?: string;
    templateId?: number;
  };
  runTime?: string;
  status?: number;
}

export interface CommunicationTypeChoice {
  key: string;
  value: string;
}

export interface ChannelTypeChoice {
  key: string;
  value: string;
}

export interface TemplateChoice {
  key: string;
  value: string;
}

// Communication API Functions
export const communicationTabApi = {
  // Create Communication
  createCommunication: async (payload: CreateCommunicationPayload): Promise<{ result: Communication }> => {
    const url = ApiEndpoints.createUrl('communication', 'communications');
    const response = await communicationApiClient.post(url, payload);
    return transformJsonToObject<{ result: Communication }>(response.data);
  },

  // Update Communication
  updateCommunication: async (communicationId: string, payload: UpdateCommunicationPayload): Promise<{ result: Communication }> => {
    const url = ApiEndpoints.createUrlWithPathVariables('communication', 'communications', [communicationId]);
    const response = await communicationApiClient.put(url, payload);
    return transformJsonToObject<{ result: Communication }>(response.data);
  },

  getCommunicationHistory: async (id: string, filters: CommunicationFilters = {}): Promise<PaginatedResponse<Communication>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('communication', 'notifications', (query) => {
      query.push('entity_id', id);
      if (filters.page) query.push('page', filters.page.toString());
      if (filters.limit) query.push('limit', filters.limit.toString());
      if (filters.communicationType) query.push('action_type', filters.communicationType);
      if (filters.remoteUtilityId) query.push('remote_utility_id', filters.remoteUtilityId.toString());
    });
    
    const response = await communicationApiClient.get<PaginatedResponse<Communication>>(url);
    return transformJsonToObject<PaginatedResponse<Communication>>(response.data);
  },
  // Get Communication Types
  getCommunicationTypes: async (): Promise<{ result: CommunicationTypeChoice[] }> => {
    const url = ApiEndpoints.createUrl('communication', 'choices/communication-types');
    const response = await communicationApiClient.get(url);
    return transformJsonToObject<{ result: CommunicationTypeChoice[] }>(response.data);
  },

  // Get Channel Types
  getChannelTypes: async (): Promise<{ result: ChannelTypeChoice[] }> => {
    const url = ApiEndpoints.createUrlWithPathVariables('communication', 'choices', ['channel_type']);
    const response = await communicationApiClient.get(url);
    return transformJsonToObject<{ result: ChannelTypeChoice[] }>(response.data);
  },

  // Get Templates by Channel Type
  getTemplatesByChannelType: async (params: {
    channelType: string;
    remoteUtilityId: number;
  }): Promise<{ result: TemplateChoice[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('communication', 'template-dropdown', (query) => {
      query.push('remote_utility_id', params.remoteUtilityId.toString());
      query.push('channel_type', params.channelType);
      return query;
    });
    
    const response = await communicationApiClient.get(url);
    return transformJsonToObject<{ result: TemplateChoice[] }>(response.data);
  },

  // Get Communication Summary
  getCommunicationSummary: async (params: {
    remoteUtilityId: number;
    entityId?: number;
    requestId?: string;
    consumerId?: string;
  }): Promise<{ result: {
    total: number;
    sent: number;
    failed: number;
    pending: number;
    byChannel: Array<{
      channel: string;
      count: number;
    }>;
    byStatus: Array<{
      status: string;
      count: number;
    }>;
  } }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters('communication', 'communications/summary', (query) => {
      query.push('remote_utility_id', params.remoteUtilityId.toString());
      if (params.entityId) query.push('entity_id', params.entityId.toString());
      if (params.requestId) query.push('request_id', params.requestId);
      if (params.consumerId) query.push('consumer_id', params.consumerId);
      return query;
    });
    
    const response = await communicationApiClient.get(url);
    return transformJsonToObject<{ result: {
      total: number;
      sent: number;
      failed: number;
      pending: number;
      byChannel: Array<{
        channel: string;
        count: number;
      }>;
      byStatus: Array<{
        status: string;
        count: number;
      }>;
    } }>(response.data);
  },

  // Resend Communication
  resendCommunication: async (communicationId: string): Promise<{ result: Communication }> => {
    const url = ApiEndpoints.createUrlWithPathVariables('communication', 'communications', [communicationId, 'resend']);
    const response = await communicationApiClient.post(url);
    return transformJsonToObject<{ result: Communication }>(response.data);
  },

  // Cancel Communication
  cancelCommunication: async (communicationId: string): Promise<{ result: Communication }> => {
    const url = ApiEndpoints.createUrlWithPathVariables('communication', 'communications', [communicationId, 'cancel']);
    const response = await communicationApiClient.post(url);
    return transformJsonToObject<{ result: Communication }>(response.data);
  },

  // Bulk Operations
  bulkDeleteCommunications: async (payload: {
    communicationIds: string[];
  }): Promise<{ result: { success: number; failed: number; errors: string[] } }> => {
    const url = ApiEndpoints.createUrl('communication', 'communications/bulk-delete');
    const response = await communicationApiClient.post(url, payload);
    return transformJsonToObject<{ result: { success: number; failed: number; errors: string[] } }>(response.data);
  },

  bulkResendCommunications: async (payload: {
    communicationIds: string[];
  }): Promise<{ result: { success: number; failed: number; errors: string[] } }> => {
    const url = ApiEndpoints.createUrl('communication', 'communications/bulk-resend');
    const response = await communicationApiClient.post(url, payload);
    return transformJsonToObject<{ result: { success: number; failed: number; errors: string[] } }>(response.data);
  },

  bulkCancelCommunications: async (payload: {
    communicationIds: string[];
  }): Promise<{ result: { success: number; failed: number; errors: string[] } }> => {
    const url = ApiEndpoints.createUrl('communication', 'communications/bulk-cancel');
    const response = await communicationApiClient.post(url, payload);
    return transformJsonToObject<{ result: { success: number; failed: number; errors: string[] } }>(response.data);
  },

  callPurpose: async () => {
    const url = ApiEndpoints.createUrl(
      "consumerCommunication", 
      "choices/call_purpose"
    );

    const response = await consumerCommunicationApiClient.get(url);
    return response.data.result ?? [];
  },

  callStatus: async () => {
    const url = ApiEndpoints.createUrl(
      "consumerCommunication", 
      "choices/call_status"
    );

    const response = await consumerCommunicationApiClient.get(url);
    return response.data.result ?? [];
  },

  createCallEntry: async (
    data: any
  ): Promise<any> => {
    console.log('communication data',data);
    const payload = {
      consumer: data.consumer,
      call_purpose: data.call_purpose,
      call_status: data.call_status,
      call_date: data.call_date,
      call_time: data.call_time,
      notes: data.notes,
    };
  
    try {
      const response = await consumerCommunicationApiClient.post(
        "calls/",
        payload
      );
      return response.data.result || response.data;
    } catch (error) {
      throw error;
    }
  },

  getCallEntryById: async (id: string,remoteUtilityId:string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "consumerCommunication", 
      "calls", 
      (query) => {
        query.push("consumer", id);
        query.push("remote_utility_id", remoteUtilityId);

        return query;
      }
    );

    const response = await consumerCommunicationApiClient.get(url);
    return response.data.result ?? [];
  },

  getAllCommunicationHistory: async (remote_utility_id: string,consumer_id: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `communication-history/${consumer_id}`,
      (query) => {
        query.push("remote_utility_id", remote_utility_id);
      }
    );
  
    const response = await cxApiClient.get(url);
    return response.data ?? [];
  },
};
